import type { DriveStep } from 'driver.js';
import { useCallback, useMemo } from 'react';
import { useManualDriver } from '/@/components/driverjs/useDriver';
import { _ } from '/@/locale';
import { MY_FIELDS_TOUR_IDS } from '../consultant-fields-tour-ids';

export function buildMyFieldsHelpTourSteps(showFieldsTour: boolean): DriveStep[] {
    if (!showFieldsTour) return [];
    return [
        {
            element: `#${MY_FIELDS_TOUR_IDS.INTRO}`,
            popover: {
                title: _('Tus propios lotes'),
                description: _(
                    'Esta es una sección privada para que puedas generar lotes y hacer pruebas de recomendaciones o reportes antes de enviárselas a un productor.'
                ),
            },
        },
        {
            element: `#${MY_FIELDS_TOUR_IDS.WIDGETS}`,
            popover: {
                title: _('El resumen de tus lotes'),
                description: _(
                    'Acá vas a ver cuántos lotes propios tenés activos, la superficie total y cómo se distribuyen por cultivo en la campaña vigente.'
                ),
            },
        },
        {
            element: `#${MY_FIELDS_TOUR_IDS.ASSIGN_BTN}`,
            popover: {
                title: _('Asignar a un productor'),
                description: _(
                    'Si uno o más lotes que creaste pertenece a un productor activo podés asignarlo de manera individual o masiva.'
                ),
            },
        },
        {
            element: `#${MY_FIELDS_TOUR_IDS.TABS}`,
            popover: {
                title: _('Tres vistas de tus lotes'),
                description: _(
                    'Seguí la campaña activa de cada lote, revisá los reportes históricos de rendimiento por híbrido o consultá las recomendaciones que generaste. Todo desde el mismo lugar.'
                ),
            },
            onHighlighted: (_element, _step, opts) => {
                opts.driver.setConfig({ ...opts.driver.getConfig(), stagePadding: 0 });
            },
            onDeselected: (_element, _step, opts) => {
                opts.driver.setConfig({ ...opts.driver.getConfig(), stagePadding: 4 });
            },
        },
    ];
}

export function useMyFieldsHelpTour(options: {
    showFieldsTour: boolean;
    onEnsureCurrentCampaignTab: () => void;
    canStartManualTour: boolean;
}) {
    const { showFieldsTour, onEnsureCurrentCampaignTab, canStartManualTour } = options;

    const fieldsTourSteps = useMemo(() => buildMyFieldsHelpTourSteps(showFieldsTour), [showFieldsTour]);

    const prepareFieldsTour = useCallback(() => {
        onEnsureCurrentCampaignTab();
    }, [onEnsureCurrentCampaignTab]);

    const { startTour } = useManualDriver(fieldsTourSteps, {
        popoverTag: _('MIS LOTES'),
        mixpanelSection: 'my-fields',
        showHelpCenterLink: true,
    });

    const startHelpTourFromIcon = useCallback(() => {
        if (!canStartManualTour) return;

        onEnsureCurrentCampaignTab();
        const appContent = document.querySelector('#app-content');
        if (appContent) {
            (appContent as HTMLElement).scrollTop = 0;
        }
        window.setTimeout(() => {
            startTour();
        }, 200);
    }, [canStartManualTour, onEnsureCurrentCampaignTab, startTour]);

    return {
        fieldsTourSteps,
        prepareFieldsTour,
        startHelpTourFromIcon,
    };
}
